import { c as _c } from "react/compiler-runtime";
import { getErrorMessage } from '@onefootprint/request';
import { useShortcut } from '@onefootprint/ui';
import useSession from '@/hooks/use-session';
import type { ChatSessionCostTarget } from '../../hooks/use-chat-session-cost';
import useChatSessionCost from '../../hooks/use-chat-session-cost';
import { costOverlayStore, useCostOverlayOpen } from './use-cost-overlay-open';
export type CostStatusBarProps = {
  chatSessionId?: string;
  investigationId?: string;
};
const formatUsd = (value: number | null) => value == null ? '—' : `$${value.toFixed(4)}`;
const formatTokens = (value: number) => value.toLocaleString('en-US');

/**
 * Risk-ops-firm-employee-only debug overlay: Ctrl+Shift+Y toggles a small monospace status
 * bar pinned to the bottom of the window that lists per-model token usage and the
 * estimated USD cost of the current chat session / investigation. Renders
 * nothing (and binds no shortcut) for anyone who isn't a risk-ops firm employee.
 */
const CostStatusBar = t0 => {
  const $ = _c(24);
  const {
    chatSessionId,
    investigationId
  } = t0;
  const {
    data: t1
  } = useSession();
  const {
    user
  } = t1;
  const isRiskOpsFirmEmployee = !!user?.isFirmEmployee && !!user?.isRiskOpsEmployee;
  const isOpen = useCostOverlayOpen();
  let t2;
  if ($[0] !== chatSessionId || $[1] !== investigationId) {
    t2 = investigationId ? {
      investigationId
    } : chatSessionId ? {
      chatSessionId
    } : undefined;
    $[0] = chatSessionId;
    $[1] = investigationId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const target = t2;
  const targetLabel = investigationId ? `investigation ${investigationId}` : chatSessionId ? `chat ${chatSessionId}` : "";
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["control", "shift", "y"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = !isRiskOpsFirmEmployee || !target;
  let t5;
  if ($[4] !== t4) {
    t5 = {
      keys: t3,
      onShortcut: _temp,
      onEscKeyDown: _temp2,
      disabled: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  useShortcut(t5);
  const {
    data,
    isLoading,
    error
  } = useChatSessionCost(target, isOpen);
  if (!isRiskOpsFirmEmployee || !isOpen || !target) {
    return null;
  }
  let t6;
  if ($[6] !== targetLabel) {
    t6 = <span className="text-tertiary max-md:min-w-0 max-md:truncate">percy cost · {targetLabel}</span>;
    $[6] = targetLabel;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <button type="button" className="text-tertiary hover:text-primary max-md:shrink-0" onClick={_temp3}>ctrl+shift+y / esc to close</button>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6) {
    t8 = <div className="flex items-center justify-between gap-3 md:gap-0 border-b border-tertiary bg-secondary px-3 py-1">{t6}{t7}</div>;
    $[9] = t6;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== isLoading) {
    t9 = isLoading ? <div className="py-1 text-tertiary">loading…</div> : null;
    $[11] = isLoading;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== error) {
    t10 = error ? <div className="py-1 text-error">{getErrorMessage(error)}</div> : null;
    $[13] = error;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== data) {
    t11 = data ? <table className="w-full border-collapse text-left tabular-nums"><thead className="text-tertiary"><tr><th className="pr-4 font-normal">model</th><th className="pr-4 text-right font-normal">in</th><th className="pr-4 text-right font-normal">out</th><th className="pr-4 text-right font-normal">cache w</th><th className="pr-4 text-right font-normal">cache r</th><th className="pr-4 text-right font-normal">total</th><th className="text-right font-normal">cost</th></tr></thead><tbody>{data.models.length === 0 ? <tr><td className="py-1 text-tertiary" colSpan={7}>no token usage recorded</td></tr> : data.models.map(_temp4)}</tbody><tfoot className="border-t border-tertiary"><tr className="font-semibold"><td className="pr-4">total</td><td className="pr-4 text-right">{formatTokens(data.totalInputTokens)}</td><td className="pr-4 text-right">{formatTokens(data.totalOutputTokens)}</td><td className="pr-4 text-right">{formatTokens(data.totalCacheCreationInputTokens)}</td><td className="pr-4 text-right">{formatTokens(data.totalCacheReadInputTokens)}</td><td className="pr-4 text-right">{formatTokens(data.totalTokens)}</td><td className="text-right">{formatUsd(data.totalCostUsd)}</td></tr></tfoot></table> : null;
    $[15] = data;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t9) {
    t12 = <div className="max-h-32 md:max-h-48 overflow-auto px-3 py-1">{t9}{t10}{t11}</div>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t8) {
    t13 = <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-tertiary bg-primary font-mono text-snippet-3 text-primary pb-[env(safe-area-inset-bottom)]">{t8}{t12}</div>;
    $[21] = t12;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
};
export default CostStatusBar;
function _temp() {
  return costOverlayStore.toggle();
}
function _temp2() {
  return costOverlayStore.set(false);
}
function _temp3() {
  return costOverlayStore.set(false);
}
function _temp4(model) {
  return <tr key={model.model}><td className="pr-4">{model.model}</td><td className="pr-4 text-right">{formatTokens(model.inputTokens)}</td><td className="pr-4 text-right">{formatTokens(model.outputTokens)}</td><td className="pr-4 text-right">{formatTokens(model.cacheCreationInputTokens)}</td><td className="pr-4 text-right">{formatTokens(model.cacheReadInputTokens)}</td><td className="pr-4 text-right">{formatTokens(model.totalTokens)}</td><td className="text-right">{formatUsd(model.costUsd)}</td></tr>;
}